import { DateTime } from 'luxon';
import type { MergeAgencyFieldValueTypes, MergeAgencyValues } from '~/api/civilsearch';
import { MERGE_FIELD_INPUT, MERGEABLE_AGENCY_FIELDS, type MergeableAgencyField } from '~/common/mergeableAgencyFields';
import type { Agency } from '~/common/types';

export { MERGE_FIELD_INPUT, MERGEABLE_AGENCY_FIELDS, type MergeableAgencyField };

/** The most agencies one merge takes; the API rejects more. */
export const MAX_MERGE_AGENCIES = 10;

/**
 * Human-readable labels for each mergeable field. Used by the merge dialog.
 */
export const MERGEABLE_AGENCY_FIELD_LABELS: Record<MergeableAgencyField, string> = {
  agencyName: 'Agency Name',
  type: 'Type',
  isPublished: 'Published',
  asbuiltsAvailable: 'As-builts Available',
  providerId: 'Provider ID',
  pdfLink: 'PDF Link',
  siteLink: 'Site Link',
  state: 'State',
  outreachEmail: 'Outreach Email',
  outreachEmailName: 'Outreach Email Name',
  publicNotes: 'Public Notes',
  doesNotRespond: 'Does Not Respond',
  chargesMapFees: 'Charges Map Fees',
  requiresLetterhead: 'Requires Letterhead',
  requiresNda: 'Requires NDA',
  hideResponseEstimate: 'Hide Estimated Response Time',
  averageMapFeePerPage: 'Average Map Fee Per Page',
  mapFeeNote: 'Map Fee Note',
  preferredContactMethod: 'Preferred Contact Method',
  prrUrl: 'PRR URL',
  portalContactFormUrl: 'Portal Contact Form URL',
  aiContactResearch: 'AI Contact Research',
  logo: 'Logo',
  restrictions: 'Restrictions',
  civilSearchTier: 'CivilSearch Tier',
  defaultCivilSearchAssigneeId: 'Default CivilSearch Assignee',
  firstFollowUpDuration: 'First Follow-Up Duration (days)',
  secondFollowUpDuration: 'Second Follow-Up Duration (days)',
  thirdFollowUpDuration: 'Third Follow-Up Duration (days)',
  fourthFollowUpDuration: 'Fourth Follow-Up Duration (days)',
  timeoutDuration: 'Timeout Duration (days)',
};

/**
 * Ordered grouping of mergeable fields into sections rendered in the merge
 * dialog. Every entry in MERGEABLE_AGENCY_FIELDS appears exactly once across
 * all sections (enforced by the test suite).
 */
export const MERGEABLE_AGENCY_FIELD_SECTIONS = [
  {
    section: 'Agency Information',
    fields: ['agencyName', 'type', 'state', 'isPublished', 'asbuiltsAvailable', 'publicNotes'],
  },
  {
    section: 'Contact',
    fields: [
      'prrUrl',
      'portalContactFormUrl',
      'preferredContactMethod',
      'defaultCivilSearchAssigneeId',
      'outreachEmail',
      'outreachEmailName',
    ],
  },
  {
    section: 'Submission Requirements',
    fields: [
      'requiresLetterhead',
      'requiresNda',
      'doesNotRespond',
      'chargesMapFees',
      'averageMapFeePerPage',
      'mapFeeNote',
    ],
  },
  {
    section: 'Follow-up & Timeout',
    fields: [
      'firstFollowUpDuration',
      'secondFollowUpDuration',
      'thirdFollowUpDuration',
      'fourthFollowUpDuration',
      'timeoutDuration',
    ],
  },
  {
    section: 'Data & Files',
    fields: ['pdfLink', 'siteLink', 'logo', 'providerId'],
  },
  {
    section: 'CivilSearch Configuration',
    fields: ['civilSearchTier', 'hideResponseEstimate', 'restrictions', 'aiContactResearch'],
  },
] as const satisfies ReadonlyArray<{
  section: string;
  fields: ReadonlyArray<MergeableAgencyField>;
}>;

/** Structural equality for merge values; null and undefined are the same "absent" value. */
export function areMergeValuesEqual(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (a === b) return true;
  // jsonb columns (`logo`, `restrictions`) arrive as distinct objects with equal contents.
  if (typeof a === 'object' && typeof b === 'object') {
    return deepEqual(a, b);
  }
  return false;
}

/**
 * Minimal structural deep-equal. Handles arrays and plain objects with
 * JSON-serializable contents (which is all the jsonb columns store). Used for
 * `logo` and `restrictions`; primitives are short-circuited above.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a as Record<string, unknown>);
    const bKeys = Object.keys(b as Record<string, unknown>);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/** One value per mergeable field; null while a field has no value. */
export type MergeFieldValues = { [F in MergeableAgencyField]: MergeAgencyFieldValueTypes[F] | null };

/**
 * Reads an agency's value for a mergeable field in the form the merge body sends it: missing
 * becomes null and the logo's `uploadedAt` becomes an ISO string, or is left out when absent.
 */
export function getMergeFieldValue<F extends MergeableAgencyField>(agency: Agency, field: F): MergeFieldValues[F] {
  const raw = (agency as unknown as Record<string, unknown>)[field];
  if (raw == null) return null;
  if (field === 'logo') {
    const { uploadedAt, ...logo } = raw as NonNullable<Agency['logo']>;
    const at: unknown = uploadedAt;
    if (at == null || at === '') return logo as MergeFieldValues[F];
    const iso = DateTime.isDateTime(at) ? at.toISO() : String(at);
    return (iso ? { ...logo, uploadedAt: iso } : logo) as MergeFieldValues[F];
  }
  return raw as MergeFieldValues[F];
}

/** True for a value the dialog shows as "empty": null, undefined, or a blank string. */
export function isEmptyMergeValue(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

/** The value an empty choice sends: a blank string on a NOT NULL text column, else null. */
function emptyMergeValue(field: MergeableAgencyField, value: unknown): unknown {
  return typeof value === 'string' && NON_NULL_MERGE_FIELDS.has(field) ? '' : null;
}

/** The first non-empty value of a field in the given order, or its empty value when none has one. */
function firstMergeValue(agencies: readonly Agency[], field: MergeableAgencyField): unknown {
  const values = agencies.map((agency) => getMergeFieldValue(agency, field));
  const nonEmpty = values.find((value) => !isEmptyMergeValue(value));
  if (nonEmpty !== undefined) return nonEmpty;
  return emptyMergeValue(
    field,
    values.find((value) => value != null),
  );
}

/** The first non-empty value of every field across the agencies, in the given order. */
export function defaultMergeValues(agencies: readonly Agency[]): MergeFieldValues {
  const values = {} as Record<MergeableAgencyField, unknown>;
  for (const field of MERGEABLE_AGENCY_FIELDS) {
    values[field] = firstMergeValue(agencies, field);
  }
  return values as MergeFieldValues;
}

/** One distinct value of a field across the agencies, with the indexes of the agencies holding it. */
export type MergeValueGroup = {
  /** The value as the first holder has it; the empty group holds null, or a blank string on a NOT NULL text column. */
  value: unknown;
  empty: boolean;
  /** Indexes into the agencies, in selection order. */
  members: number[];
};

/**
 * Groups a field's values into distinct values, empty values forming one group. Sorted by holder
 * count, most first, then by the selection order of each group's first holder.
 */
export function groupFieldValues(agencies: readonly Agency[], field: MergeableAgencyField): MergeValueGroup[] {
  const groups: MergeValueGroup[] = [];
  agencies.forEach((agency, index) => {
    const value = getMergeFieldValue(agency, field);
    const empty = isEmptyMergeValue(value);
    const group = groups.find((g) => (empty ? g.empty : !g.empty && areMergeValuesEqual(g.value, value)));
    if (group) group.members.push(index);
    else groups.push({ value: empty ? emptyMergeValue(field, value) : value, empty, members: [index] });
  });
  return groups.sort((a, b) => b.members.length - a.members.length || a.members[0] - b.members[0]);
}

/** Index of the group that holds `value`, or -1 when no agency holds it. */
export function findMergeValueGroup(groups: readonly MergeValueGroup[], value: unknown): number {
  const empty = isEmptyMergeValue(value);
  return groups.findIndex((g) => (empty ? g.empty : !g.empty && areMergeValuesEqual(g.value, value)));
}

/** A field agrees when the agencies hold at most one distinct non-empty value. */
export function isAgreedField(groups: readonly MergeValueGroup[]): boolean {
  return groups.filter((g) => !g.empty).length <= 1;
}

export type MergeFieldSummary = {
  fieldCount: number;
  /** Fields where the agencies hold two or more distinct non-empty values. */
  differingFields: MergeableAgencyField[];
  /** Fields with no value on any agency. */
  emptyFields: MergeableAgencyField[];
};

/** Counts the differing and all-empty fields across the agencies. */
export function summarizeFields(agencies: readonly Agency[]): MergeFieldSummary {
  const differingFields: MergeableAgencyField[] = [];
  const emptyFields: MergeableAgencyField[] = [];
  for (const field of MERGEABLE_AGENCY_FIELDS) {
    const groups = groupFieldValues(agencies, field);
    if (!isAgreedField(groups)) differingFields.push(field);
    if (groups.every((g) => g.empty)) emptyFields.push(field);
  }
  return { fieldCount: MERGEABLE_AGENCY_FIELDS.length, differingFields, emptyFields };
}

/**
 * Re-defaults the values after the agency list changes. Fields in `keep` are left alone; a field in
 * `touched` keeps its value while a remaining agency holds it; every other field takes the first
 * non-empty value of the remaining agencies.
 */
export function reconcileMergeValues(
  values: MergeFieldValues,
  agencies: readonly Agency[],
  keep: ReadonlySet<MergeableAgencyField>,
  touched: ReadonlySet<MergeableAgencyField>,
): MergeFieldValues {
  const next = { ...values } as Record<MergeableAgencyField, unknown>;
  for (const field of MERGEABLE_AGENCY_FIELDS) {
    if (keep.has(field)) continue;
    const held = findMergeValueGroup(groupFieldValues(agencies, field), values[field]) !== -1;
    if (!touched.has(field) || !held) next[field] = firstMergeValue(agencies, field);
  }
  return next as MergeFieldValues;
}

/** Fields whose column is NOT NULL. A cleared text field among them sends an empty string. */
const NON_NULL_MERGE_FIELDS = new Set<MergeableAgencyField>([
  'agencyName',
  'type',
  'isPublished',
  'asbuiltsAvailable',
  'state',
  'doesNotRespond',
  'chargesMapFees',
  'requiresLetterhead',
  'requiresNda',
  'hideResponseEstimate',
  'averageMapFeePerPage',
  'mapFeeNote',
  'preferredContactMethod',
  'civilSearchTier',
  'firstFollowUpDuration',
  'secondFollowUpDuration',
  'thirdFollowUpDuration',
  'fourthFollowUpDuration',
  'timeoutDuration',
]);

/** Text fields that must hold more than whitespace. */
const NON_BLANK_MERGE_FIELDS = new Set<MergeableAgencyField>(['agencyName', 'type']);

/** Converts a text control's string to the field's value: empty is null on a nullable column. */
export function textToMergeValue(field: MergeableAgencyField, text: string): string | null {
  return text === '' && !NON_NULL_MERGE_FIELDS.has(field) ? null : text;
}

/** Returns why a merged value can't be sent, or null when it is valid. */
export function getMergeFieldError(field: MergeableAgencyField, value: unknown): string | null {
  const input = MERGE_FIELD_INPUT[field];
  if (value == null) {
    if (!NON_NULL_MERGE_FIELDS.has(field)) return null;
    return input.kind === 'number' ? 'Enter a number.' : 'Choose or enter a value.';
  }
  if (NON_BLANK_MERGE_FIELDS.has(field) && typeof value === 'string' && value.trim() === '') {
    return 'Choose or enter a value.';
  }
  if (input.kind === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number.';
    if (input.integer && !Number.isInteger(value)) return 'Enter a whole number.';
  }
  return null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isHttpUrl(text: string): boolean {
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Returns why a value typed in a field's Custom control doesn't fit its input, or null. Checked
 * only while Custom is chosen, so an agency's existing value is never blocked here.
 */
export function getCustomInputError(field: MergeableAgencyField, value: unknown): string | null {
  const input = MERGE_FIELD_INPUT[field];
  if (input.kind === 'number' && typeof value === 'number' && value < 0) return 'Enter 0 or more.';
  if (typeof value !== 'string' || value.trim() === '') return null;
  if (input.kind === 'url' && !isHttpUrl(value.trim())) return 'Enter a full URL starting with http:// or https://.';
  if (input.kind === 'email' && !EMAIL_PATTERN.test(value.trim()))
    return 'Enter an email address like name@agency.gov.';
  if (input.kind === 'select' && !input.options.some((option) => option.value === value)) {
    return 'Choose one of the listed values.';
  }
  return null;
}

/** Builds the merge body's `values`; call it only once `getMergeFieldError` passes for every field. */
export function toMergeAgencyValues(values: MergeFieldValues): MergeAgencyValues {
  return { ...values } as MergeAgencyValues;
}

/**
 * Format a value for display in the merge dialog.
 *  - null / undefined → "—"
 *  - boolean → "Yes" / "No"
 *  - string → trimmed; empty string → "—"
 *  - everything else → String(value)
 */
export function formatFieldValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? '—' : trimmed;
  }
  return String(value);
}
